import { useSeoTags } from "../useSeoTags";
import "../marketMyths.css";
import "../humanArticleTemplate.css";

// WAVE 1, 2026-10-28 -- humanized rewrite of the Turnover evidence doc.
// Isolated, unlinked, noindexed preview. Numbers re-verified fresh against
// feature_library (turnover_20d_avg) and data/feature_search_phase3.json
// (turnover_abn) before writing.

const PER_SEGMENT: { label: string; value: number }[] = [
  { label: "Mid-cap", value: 0.0009 },
  { label: "Large-cap", value: -0.0093 },
  { label: "Micro-cap", value: -0.0045 },
  { label: "Small-cap", value: -0.0116 },
];

function SegmentIcChart() {
  const width = 640;
  const rowH = 40;
  const height = PER_SEGMENT.length * rowH + 20;
  const labelW = 110;
  const plotW = width - labelW - 20;
  const midX = labelW + plotW / 2;
  const maxAbs = Math.max(...PER_SEGMENT.map((d) => Math.abs(d.value)));
  const scale = (plotW / 2 - 40) / maxAbs;

  return (
    <figure className="hatpl-chart">
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img"
           aria-label="Per-segment correlation between trailing turnover and forward return: positive only in mid-cap, negative in the other three">
        <line x1={midX} y1={6} x2={midX} y2={height - 6} className="hatpl-chart-zeroline" />
        {PER_SEGMENT.map((d, i) => {
          const y = 10 + i * rowH;
          const w = Math.abs(d.value) * scale;
          const x = d.value >= 0 ? midX : midX - w;
          return (
            <g key={d.label}>
              <text x={0} y={y + 14} className="hatpl-chart-label">{d.label}</text>
              <rect x={x} y={y + 2} width={Math.max(w, 1)} height={16} className={d.value >= 0 ? "hatpl-chart-bar" : "hatpl-chart-bar-neg"} />
              <text x={d.value >= 0 ? x + w + 8 : x - 8} y={y + 14}
                    textAnchor={d.value >= 0 ? "start" : "end"} className="hatpl-chart-value">
                {d.value >= 0 ? "+" : ""}{d.value.toFixed(4)}
              </text>
            </g>
          );
        })}
      </svg>
      <figcaption>
        The level-of-turnover effect, split by market-cap segment. One positive bar, three negative — the
        pooled average (+0.041) is positive only because the segments are combined; within three of the four
        segments tested, more turnover goes with lower forward returns, not higher.
      </figcaption>
      <div className="hatpl-asof">
        As of 2026&#8209;08&#8209;08 (blessing run) · re-validated on a slow cadence, not daily.
      </div>
    </figure>
  );
}

const CANONICAL = "https://tickerpicker.in/market-myths?evidence=turnover";

export function TurnoverArticlePage({ onBack }: { onBack: () => void }) {
  useSeoTags({
    title: "High-turnover stocks move more — or do they? Two questions, two different answers — Tickerpicker",
    description:
      "Heavy trading in a stock is supposed to mean something. We tested two versions of that claim and neither " +
      "one holds up cleanly once you look inside the average.",
    canonical: CANONICAL,
    ogType: "article",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "High-turnover stocks move more — or do they?",
      url: CANONICAL,
      publisher: { "@type": "Organization", name: "Tickerpicker" },
      isPartOf: { "@type": "WebPage", name: "Market Myths: Tested", url: "https://tickerpicker.in/market-myths" },
    },
  });

  return (
    <div className="hatpl-shell">
      <nav className="hatpl-nav">
        <a href="/" className="hatpl-brand"><span className="dot" />TickerPicker</a>
        <div className="mm-evid-crumbs">
            <a href="/">Home</a>
            <span className="mm-crumb-sep">/</span>
            <button className="mm-back" onClick={onBack}>&larr; Back to Market Myths</button>
          </div>
      </nav>

      <article className="hatpl-article">
        <div className="hatpl-kicker">Market Myths: Tested — trading turnover</div>
        <h1>High-turnover stocks move more — or do they?</h1>

        <p>
          "Turnover" gets used two different ways by traders, and they're not the same claim. One is about
          habit: a stock that always trades heavily versus one that rarely does. The other is about a moment: a
          sudden spike in trading relative to that stock's own normal. We tested both. Neither one survives
          contact with the data cleanly.
        </p>

        <h2>The habit — a weak, inconsistent positive</h2>

        <p>
          A stock's typical 20-day trading volume carries a correlation of about 0.041 with its forward return,
          averaged across 2,454 trading days — a number significant enough that it's a real, blessed input in
          our production models. Taken at face value, that's a usable signal.
        </p>

        <SegmentIcChart />

        <p>
          Look inside it and the story changes. Sort stocks into deciles by turnover and the forward returns
          don't line up in order — our monotonicity check comes back at −0.68, badly broken. And when you split
          by market-cap segment instead of pooling everyone together, the positive average turns out to be
          almost entirely a mid-cap phenomenon; large, small, and micro caps all run negative. A number that
          looks clean pooled and falls apart split is usually telling you the pooling is doing the work, not the
          signal.
        </p>

        <h2>The moment — a spike that disagrees with itself</h2>

        <p>
          A sudden jump in turnover relative to a stock's own recent normal shows a real, significant raw
          correlation with forward return: about −0.013, meaning an abnormal spike tends to come with slightly
          lower returns next. But strip out what our other features already explain, and what's left over flips
          sign entirely — to about +0.006, also significant on its own. The raw signal and its own leftover
          point in opposite directions, which is disqualifying regardless of how clean either number looks in
          isolation.
        </p>

        <blockquote className="hatpl-pullquote">
          A blessed feature isn't automatically a reliable one.
        </blockquote>

        <p>
          The habit-level turnover feature stays in production — it clears our own blessing bar, and a weak,
          inconsistent contributor is still a contributor among many. But neither version of "turnover tells you
          something" is a standalone edge, and the spike version doesn't make it into the model at all.
        </p>

        <hr className="hatpl-rule" />

        <details className="hatpl-numbers">
          <summary>The full numbers</summary>
          <h3>Level — turnover_20d_avg</h3>
          <table>
            <tbody>
              <tr><td>Pooled correlation (IC)</td><td>+0.0408</td></tr>
              <tr><td>t-statistic</td><td>13.76</td></tr>
              <tr><td>Trading dates</td><td>2,454</td></tr>
              <tr><td>Decile monotonicity</td><td>&minus;0.68</td></tr>
              <tr><td>Per-segment IC — mid-cap</td><td>+0.0009</td></tr>
              <tr><td>Per-segment IC — large-cap</td><td>&minus;0.0093</td></tr>
              <tr><td>Per-segment IC — micro-cap</td><td>&minus;0.0045</td></tr>
              <tr><td>Per-segment IC — small-cap</td><td>&minus;0.0116</td></tr>
              <tr><td>Blessed into production</td><td>yes, 2026-08-08</td></tr>
            </tbody>
          </table>
          <h3>Spike — turnover_abn</h3>
          <table>
            <tbody>
              <tr><td>Raw out-of-sample IC</td><td>&minus;0.0126</td></tr>
              <tr><td>t-statistic</td><td>&minus;3.27</td></tr>
              <tr><td>FDR-adjusted p</td><td>0.0013</td></tr>
              <tr><td>Incremental (residual) mean IC</td><td>+0.0062</td></tr>
              <tr><td>Incremental FDR-adjusted p</td><td>0.0016</td></tr>
              <tr><td>Ships?</td><td>no — redundant (sign disagreement)</td></tr>
            </tbody>
          </table>
          <p className="hatpl-source">
            Sources: <code>feature_library</code> (live production blessing record, <code>turnover_20d_avg</code>);
            <code>data/feature_search_phase3.json</code> (<code>turnover_abn</code>, pre-registered sweep,
            <code>backend/feature_search_phase3.py</code>).
          </p>
        </details>

        <div className="mm-evid-footnav">
          <button className="mm-back" onClick={onBack}>&larr; Back to Market Myths</button>
        </div>
      </article>
    </div>
  );
}
